/**
 * inboundDatevAccess — fail-closed Sichtbarkeits-Logik für den Eingangsbelege-
 * DATEV-Belegtransfer (IDV-6, InboxPage, architecture-inbound-datev.md §5.5).
 *
 * Single source of truth dafür, OB der „Eingangsbelege übergeben"-Abschnitt in
 * `InboxPage` sichtbar ist. Muster 1:1 `datevBelegeAccess.ts` (gleiche zwei
 * Freischalt-Pfade, gleiche Beta-Tenant-UID):
 *
 *   1. Beta-Tenant-Allowlist: der Test-Tenant sieht den Abschnitt LIVE, unabhängig
 *      vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_INBOUND_DATEV_EXPORT_ENABLED==='true'`.
 *
 * „Owner ODER aktiver StB" (architecture-inbound-datev.md §5.5) ist bereits durch
 * `effectiveScopeUid` abgebildet: der Wert ist die Owner-UID sowohl beim Owner
 * selbst als auch bei einem StB mit aktiv ausgewähltem Mandanten-Tenant
 * (`AuthContext::effectiveScopeUid`) — es gibt also KEINEN separaten Rollen-Check
 * hier (anders als `useIsReadOnly`, der Schreib-AFFORDANCES für `advisor`
 * ausblendet; der DATEV-Export ist bewusst KEIN Schreibpfad auf `inbound_documents`
 * selbst, sondern ein Export-Trigger — s. `InboundDatevExportSection`-Kopfkommentar).
 * Die harte Autorisierungsprüfung bleibt server-seitig (`buildDatevBelegePaket`,
 * Owner ODER aktives `tenant_members`-Member, §5.3/§9).
 *
 * Tier-Term (Pricing-Repackaging PR-T4, architecture-pricing-repackaging.md §3.2/§3.2b):
 * ab GA **Ultra**-Differenzierer — ABER nur im **Owner-Kontext**. Im
 * **Advisor-Kontext** (StB im Mandanten-Tenant, `AdvisorDatevPage`) bleibt das Gate
 * rein Loop-B-/Kanzlei-gegatet OHNE Owner-Tier-Term (§3.2b): ein StB darf nie durch
 * den fehlenden Ultra-Status seines Mandanten ausgesperrt werden. Deshalb der
 * explizite `isAdvisorContext`-Parameter. Inert, solange das Flag live `false`/unset
 * ist ⇒ nur die Beta-Allowlist greift, byte-identisch zum IST.
 *
 * Go-Live (GL-1 XML-Realtest im DATEV-Testkonto MUSS bestanden sein, s. §7/§19):
 * `REACT_APP_INBOUND_DATEV_EXPORT_ENABLED` bleibt bis dahin auf `false`/unset —
 * dieses Modul ist inert, solange weder Flag noch Allowlist greifen.
 *
 * @module inboundDatevAccess
 */

import type { Tier } from './entitlements';

/**
 * Beta-Erprobung Eingangsbelege-DATEV-Export — entfernen/erweitern, wenn allgemein
 * freigegeben. Identische UID wie `DATEV_BELEGE_BETA_TENANT_UIDS`/
 * `INBOUND_BETA_TENANT_UIDS` (`b3DRR1Cj63cRjdJg5VCEUpBL7hk1` = Test-Account
 * daniel_eich@outlook.com). HARTKODIERT für die kontrollierte Live-Erprobung; vor
 * allgemeiner Freigabe (Flag = 'true' für alle) wieder leeren.
 */
export const INBOUND_DATEV_EXPORT_BETA_TENANT_UIDS: readonly string[] = [
  'b3DRR1Cj63cRjdJg5VCEUpBL7hk1',
];

/**
 * Allgemeines Feature-Flag für den Eingangsbelege-DATEV-Export (Functions-
 * Gegenstück: `INBOUND_DATEV_EXPORT_ENABLED`, IDV-5-Subgate — VOR Doc-Load geprüft,
 * §5.3). Fail-closed: nur bei explizitem `'true'` aktiv. Modul-Konstante, einmalig
 * beim Bundle-Load ausgewertet. Muss im Gleichschritt mit dem Function-seitigen
 * Flag gesetzt werden, sonst zeigt die UI einen Button, dessen Aufruf die
 * Function mit `feature_disabled` (Skip) beantwortet.
 */
export const INBOUND_DATEV_EXPORT_FLAG: boolean =
  process.env.REACT_APP_INBOUND_DATEV_EXPORT_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob der Eingangsbelege-DATEV-Export-Abschnitt für einen
 * Tenant-Scope sichtbar ist.
 *
 * WICHTIG: gated auf `effectiveScopeUid` (den Tenant-SCOPE), NICHT auf
 * `currentUser.uid` — analog `canUseDatevBelege`/`canUseInbound`. Rein
 * kosmetisches Client-Gating; die harte Sperre bleibt die Function-seitige
 * Auth-/Autorisierungs-Prüfung (`buildDatevBelegePaket`).
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()` (oder null/undefined,
 *                   solange noch nicht aufgelöst).
 * @param tier - effektiver Tier des Owners (`resolveTier(sellerData)` aus
 *               `entitlements.ts`). Nur im Owner-Kontext ausgewertet.
 * @param isAdvisorContext - `true`, wenn der aktuelle Nutzer ein Steuerberater im
 *               Mandanten-Kontext ist (§3.2b). Dann KEIN Owner-Tier-Term.
 * @returns `true`, wenn `scopeUid` auf der Beta-Tenant-Allowlist steht ODER
 *          (im Advisor-Kontext) das Flag aktiv ist ODER (im Owner-Kontext) das
 *          Flag aktiv ist UND der Owner den Ultra-Tier hat. Sonst `false`.
 */
export function canUseInboundDatev(
  scopeUid: string | null | undefined,
  tier: Tier,
  isAdvisorContext: boolean,
): boolean {
  if (!!scopeUid && INBOUND_DATEV_EXPORT_BETA_TENANT_UIDS.includes(scopeUid)) {
    return true;
  }
  // §3.2b Advisor-Kontext: reines Loop-B-/Kanzlei-Gate, KEIN Owner-Tier-Term.
  if (isAdvisorContext) {
    return INBOUND_DATEV_EXPORT_FLAG;
  }
  // Owner-Kontext: allgemeine Freigabe erst ab Ultra.
  return INBOUND_DATEV_EXPORT_FLAG && tier === 'ultra';
}
